/**
 * DemoLinkContext v2.3
 * 
 * Manages the shareable demo link mode:
 * - Validates demo tokens
 * - Locks app into read-only demo mode
 * - Enforces Guided View settings from link
 * - Prevents write actions
 * - Shows Demo Mode badge
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { validateDemoLink, parseDemoTokenFromUrl } from '../guided/shareLinks';

const DemoLinkContext = createContext(null);

export const DemoLinkProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoConfig, setDemoConfig] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Check for demo token on mount and route changes
  useEffect(() => {
    const checkDemoToken = () => {
      // Only check on /demo route
      if (!location.pathname.startsWith('/demo')) {
        setIsValidating(false);
        return;
      }
      
      const token = parseDemoTokenFromUrl();
      
      if (!token) {
        setIsValidating(false);
        return;
      }
      
      const validation = validateDemoLink(token);
      
      if (!validation.valid) {
        if (validation.expired) {
          setIsExpired(true);
        }
        setIsDemoMode(false);
        setDemoConfig(null);
      } else {
        setIsDemoMode(true);
        setIsExpired(false);
        setDemoConfig({
          persona: validation.persona,
          mode: validation.mode,
          startStep: validation.startStep,
          autoplay: validation.autoplay,
          expiresAt: validation.expiresAt,
          createdBy: validation.createdBy
        });
      }
      
      setIsValidating(false);
    };
    
    checkDemoToken();
  }, [location.pathname, location.search]);

  // Block write actions in demo mode
  const blockWriteAction = useCallback((actionName) => {
    if (isDemoMode) {
      console.warn(`[Demo Mode] Write action blocked: ${actionName}`);
      return true;
    }
    return false;
  }, [isDemoMode]);

  // Exit demo mode
  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
    setDemoConfig(null);
    navigate('/login');
  }, [navigate]);

  const value = {
    isDemoMode,
    demoConfig,
    isExpired,
    isValidating,
    blockWriteAction,
    exitDemoMode
  };

  return (
    <DemoLinkContext.Provider value={value}>
      {children}
    </DemoLinkContext.Provider>
  );
};

export const useDemoLink = () => {
  const context = useContext(DemoLinkContext);
  if (!context) {
    return {
      isDemoMode: false,
      demoConfig: null,
      isExpired: false,
      isValidating: false,
      blockWriteAction: () => false,
      exitDemoMode: () => {}
    };
  }
  return context;
};

export default DemoLinkContext;
